import { SortOrder, type SearchResultSortParameter } from '@haus-storefront-react/shared-types'
import { normalizeFacetValueIds, parseDataAttributes } from '../../utilities/utilities'
import type { ProductListConfigStyles } from '../product-list/_propsFn'

export interface ProductCarouselAttributes {
  facetValueIds?: string[]
  itemsPerView: number
  productListIdentifier: string
  showCta?: boolean
  showInStock?: boolean
  showPrice?: boolean
  showBadges?: boolean
  showProgressBar?: boolean
  sort: SearchResultSortParameter
  take: number
  configStyles?: ProductListConfigStyles
}

function isProductListConfigStyles(value: unknown): value is ProductListConfigStyles {
  return typeof value === 'object' && value !== null
}

function isSearchResultSortParameter(value: unknown): value is SearchResultSortParameter {
  return typeof value === 'object' && value !== null
}

function getSortParameter(sortBy: unknown): SearchResultSortParameter {
  if (sortBy === 'popularity_desc') {
    return { popularity: SortOrder.Desc }
  }

  if (sortBy === 'name_desc') {
    return { name: SortOrder.Desc }
  }

  if (sortBy === 'price_asc') {
    return { price: SortOrder.Asc }
  }

  if (sortBy === 'price_desc') {
    return { price: SortOrder.Desc }
  }

  return { name: SortOrder.Asc }
}

/**
 * Normalize product-carousel widget data attributes into typed React props.
 */
export function getProductCarouselAttributes(attrs: NamedNodeMap): ProductCarouselAttributes {
  const base = parseDataAttributes(attrs)
  const facetValueIds = normalizeFacetValueIds(base.facetValueIds)
  const itemsPerView = typeof base.itemsPerView === 'number' ? base.itemsPerView : 4
  const take = typeof base.take === 'number' ? base.take : 8
  const productListIdentifier =
    typeof base.productListIdentifier === 'string' && base.productListIdentifier.length > 0
      ? base.productListIdentifier
      : 'product-carousel'
  const showCta = base.showCta === 'yes' || base.showCta === true
  const showInStock = base.showInStock === 'yes' || base.showInStock === true
  const showPrice = base.showPrice === 'yes' || base.showPrice === true
  const showProgressBar = base.showProgressBar === 'yes' || base.showProgressBar === true
  const showBadges = base.showBadges === 'yes' || base.showBadges === true
  const parsedSort = getSortParameter(base.sortBy)

  return {
    facetValueIds,
    itemsPerView,
    productListIdentifier,
    showCta,
    showInStock,
    showPrice,
    showBadges,
    showProgressBar,
    sort: isSearchResultSortParameter(parsedSort) ? parsedSort : { name: SortOrder.Asc },
    take,
    configStyles: isProductListConfigStyles(base.configStyles) ? base.configStyles : undefined,
  }
}

/**
 * Parse DOM data attributes for the product-carousel widget entry point.
 */
export default function propsFn(dataAttributes: NamedNodeMap): ProductCarouselAttributes {
  return getProductCarouselAttributes(dataAttributes)
}
